import { z } from 'zod';
/**
 * MultiComponentApp contracts for the factory producer path.
 *
 * Source-of-truth for the worker-consumed launcher shape is
 * lumeniq-launcher `src/launcher/app.py` (`MultiComponentApp`,
 * `ServiceSpec`, and `load_app_spec`). OCNexus keeps a small canonical
 * intermediate shape (`appId/components`) for factory fan-out, then lowers
 * each component to the launcher `app_id/services` payload persisted on
 * pipeline_runs.task_graph. Both shapes are validated here so producer drift
 * is caught before the worker claims a run.
 */
export const multiComponentFailureStrategySchema = z.enum([
    'continue_unblocked',
    'halt_on_failure',
]);
export const multiComponentAppComponentSchema = z.object({
    name: z.string().trim().min(1),
    profile: z.string().trim().min(1),
    spec: z.string().trim().min(1),
    dependsOn: z.array(z.string().trim().min(1)),
    estimatedDurationSec: z.number().int().nonnegative(),
});
export const multiComponentAppSchema = z.object({
    appId: z.string().trim().min(1),
    appName: z.string().trim().min(1).optional(),
    failureStrategy: multiComponentFailureStrategySchema,
    components: z.array(multiComponentAppComponentSchema),
    estimatedTotalSec: z.number().int().nonnegative(),
}).superRefine((app, ctx) => {
    const seen = new Set();
    for (const [index, component] of app.components.entries()) {
        if (seen.has(component.name)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['components', index, 'name'],
                message: `duplicate component name "${component.name}"`,
            });
        }
        seen.add(component.name);
    }
    for (const [index, component] of app.components.entries()) {
        for (const [depIndex, dep] of component.dependsOn.entries()) {
            if (!seen.has(dep)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['components', index, 'dependsOn', depIndex],
                    message: `dependsOn target "${dep}" is not a component`,
                });
            }
        }
    }
});
export const launcherServiceDependencySchema = z.union([
    z.string().trim().min(1),
    z.object({
        service_id: z.string().trim().min(1),
        dependency_type: z.string().trim().min(1).optional(),
        wait_condition: z.string().trim().min(1).optional(),
    }),
]);
export const launcherServiceSpecSchema = z.object({
    service_id: z.string().trim().min(1),
    name: z.string().trim().min(1).optional(),
    type: z.string().trim().min(1),
    profile: z.string().trim().min(1),
    spec: z.string().trim().min(1).optional(),
    spec_file: z.string().trim().min(1).optional(),
    depends_on: z.array(launcherServiceDependencySchema).optional(),
    outputs: z.array(z.string().trim().min(1)).optional(),
}).passthrough().superRefine((service, ctx) => {
    if (!service.spec && !service.spec_file) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['spec'],
            message: 'service must set either spec or spec_file',
        });
    }
});
export const launcherMultiComponentAppSchema = z.object({
    app_id: z.string().trim().min(1),
    app_name: z.string().trim().min(1).optional(),
    architecture: z.string().trim().min(1).optional(),
    services: z.array(launcherServiceSpecSchema),
    deployment: z.unknown().optional(),
    integration_tests: z.unknown().optional(),
}).passthrough();
export function parseLauncherMultiComponentApp(value) {
    return launcherMultiComponentAppSchema.parse(value);
}
export function parseMultiComponentApp(value) {
    return multiComponentAppSchema.parse(value);
}
//# sourceMappingURL=multi-component-app.js.map